import Info from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { ThemeOptions, alpha } from '@mui/material/styles';
import colors from './colors';

export const commonThemeOptions: ThemeOptions = {
  typography: {
    button: { textTransform: 'none' },
    fontFamily: `Inter Variable, sans-serif`,
    display1: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 2.5,
    },
    display2: {
      fontWeight: 600,
      fontSize: '1.8125rem',
      lineHeight: 2.2475,
    },
    h1: {
      fontWeight: 700,
      lineHeight: 2.031,
      fontSize: '1.5625rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5075,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.25125,
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.25125,
    },
    overline: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.995,
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.245,
    },
  },
  components: {
    MuiIconButton: {
      defaultProps: {
        disableRipple: false,
        disableFocusRipple: false,
        disableTouchRipple: false,
        focusRipple: false,
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color: ownerState.color === 'default' ? theme.palette.action.active : ownerState.color,
          '&:focus': {
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: '8px',
          },
          '&:hover': {
            borderRadius: '8px',
          },
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
        disableTouchRipple: false,
        focusRipple: false,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: 'none',
          '&.MuiButton-sizeLarge': {
            padding: '10px 22px',
          },
          '&.MuiButton-sizeMedium': {
            padding: '8px 16px',
          },
          '&.MuiButton-sizeSmall': {
            padding: '4px 10px',
          },
          '&:focus': {
            border: `1px solid ${theme.palette.primary.main}`,
            boxShadow: `inset 0 0 0 1px ${theme.palette.primary.contrastText}`,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: false,
        focusRipple: false,
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          boxShadow: 'none',
          borderRadius: '8px',
          '&:hover': {
            boxShadow: 'none',
            '&.MuiButton-contained': {
              background: ownerState.color && ownerState.color !== 'inherit' && theme.palette[ownerState.color].dark,
            },
            '&.MuiButton-outlined .MuiButton-text': {
              background:
                ownerState.color && ownerState.color !== 'inherit' && alpha(theme.palette[ownerState.color].main, 0.04),
            },
            '&.MuiButton-outlined': {
              borderColor:
                ownerState.color && ownerState.color !== 'inherit' && alpha(theme.palette[ownerState.color].main, 0.6),
            },
          },
          '&.MuiButton-sizeLarge': {
            padding: '10px 22px',
          },
          '&.MuiButton-sizeMedium': {
            padding: '8px 16px',
          },
          '&.MuiButton-sizeSmall': {
            padding: '4px 10px',
          },
          '&:focus': {
            border: `1px solid ${theme.palette.primary.main}`,
            boxShadow: `inset 0 0 0 1px ${theme.palette.primary.contrastText}`,
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color:
            ownerState.color === 'default'
              ? theme.palette.text.primary
              : theme.palette[ownerState.color ?? 'primary'].subtleContrast,
          '&.MuiChip-outlined': {
            color:
              ownerState.color === 'default'
                ? theme.palette.text.primary
                : theme.palette[ownerState.color ?? 'primary'].main,
            borderColor:
              ownerState.color === 'default'
                ? theme.palette.interactiveBorder
                : theme.palette[ownerState.color ?? 'primary'].main,
            '&:hover': {
              backgroundColor:
                ownerState.color === 'default'
                  ? theme.palette.action.hover
                  : alpha(theme.palette[ownerState.color ?? 'primary'].main, theme.palette.action.hoverOpacity),
            },
            '&:focus': {
              boxShadow: 'none',
              backgroundColor:
                ownerState.color === 'default'
                  ? theme.palette.action.focus
                  : alpha(theme.palette[ownerState.color ?? 'primary'].main, 0.3),
            },
          },
          '&.MuiChip-filled': {
            backgroundColor:
              ownerState.color === 'default'
                ? theme.palette.action.selected
                : theme.palette[ownerState.color ?? 'primary'].subtle,
            '&:hover': {
              backgroundColor:
                ownerState.color === 'default'
                  ? theme.palette.action.selectedHover
                  : theme.palette[ownerState.color ?? 'primary'].subtleHover,
            },
            '&:focus': {
              backgroundColor:
                ownerState.color === 'default'
                  ? theme.palette.action.selectedFocus
                  : theme.palette[ownerState.color ?? 'primary'].subtleHover,
              border: 'none',
              boxShadow: 'none',
            },
            '&.MuiChip-colorPrimary': {
              backgroundColor: theme.palette.primary.main,
              '& .MuiChip-label': {
                color: theme.palette.primary.contrastText,
              },
              '& .MuiChip-icon': {
                color: theme.palette.primary.contrastText,
              },
              // TODO: Change background color of whole chip/icons on delete hover.
              '& .MuiChip-deleteIcon': {
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  color: theme.palette.primary.subtleHover,
                },
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&.Mui-focusVisible': {
                backgroundColor: theme.palette.primary.subtleHover,
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                },
                '& .MuiChip-icon': {
                  color: theme.palette.primary.main,
                },
                '& .MuiChip-label': {
                  color: theme.palette.primary.subtleContrast,
                },
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.primary.subtle,
                '& .MuiChip-label': {
                  color: theme.palette.primary.subtleContrast,
                },
                '& .MuiChip-icon': {
                  color: theme.palette.primary.main,
                },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                },
              },
            },
          },
          '& .MuiChip-icon': {
            color:
              ownerState.color && ownerState.color !== 'default' && ownerState.color !== 'primary'
                ? theme.palette[ownerState.color].main
                : undefined,
          },
          '& .MuiChip-deleteIcon': {
            color:
              ownerState.color === 'default'
                ? theme.palette.action.active
                : theme.palette[ownerState.color ?? 'primary'].main,
            '&:hover': {
              color:
                ownerState.color === 'default'
                  ? theme.palette.text.primary
                  : theme.palette[ownerState.color ?? 'primary'].subtleContrast,
            },
          },
          fontSize: '0.8125rem',
          fontWeight: 500,
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          info: <Info />,
          error: <ErrorIcon />,
          success: <CheckCircleIcon />,
          warning: <ReportProblemIcon />,
        },
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          backgroundColor: theme.palette[ownerState.severity ? ownerState.severity : 'info'].subtle,
          color: theme.palette[ownerState.severity ? ownerState.severity : 'info'].subtleContrast,
          borderRadius: '8px',
          alignItems: 'center',
        }),
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: { boxShadow: 'none' },
      },
    },
    MuiToggleButton: {
      defaultProps: {
        focusRipple: false,
      },
      styleOverrides: {
        root: { borderRadius: '8px' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          padding: 6,
          marginBottom: '5px',
          '&:hover': {
            // eslint-disable-next-line no-nested-ternary
            backgroundColor: ownerState.disabled
              ? 'none'
              : ownerState.color && ownerState.checked
              ? alpha(
                  theme.palette[ownerState.color === 'default' ? 'primary' : ownerState.color].main,
                  theme.palette.action.hoverOpacity
                )
              : theme.palette.action.hover,
            borderRadius: '19px',
          },
          '& .MuiSwitch-switchBase': {
            padding: 8,
            '&.Mui-disabled + .MuiSwitch-track': {
              backgroundColor: theme.palette.action.disabledBackground,
              opacity: 1,
            },
            '&:hover': {
              background: 'none',
            },
            '&.Mui-focusVisible': {
              background:
                ownerState.color && ownerState.checked
                  ? alpha(theme.palette[ownerState.color === 'default' ? 'primary' : ownerState.color].main, 0.3)
                  : theme.palette.action.focus,
              borderRadius: '19px',
            },
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor:
                  ownerState.color && ownerState.color !== 'default'
                    ? theme.palette[ownerState.color].main
                    : theme.palette.primary.main,
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: theme.palette.action.disabledBackground,
              },
              '&:hover': {
                background: 'none',
              },
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            color:
              ownerState.color && ownerState.color !== 'default'
                ? theme.palette[ownerState.color].contrastText
                : theme.palette.primary.contrastText,
            width: 22,
            height: 22,
            boxShadow: 'none',
          },
          '& .MuiSwitch-track': {
            borderRadius: 38 / 2,
            backgroundColor: theme.palette.action.active,
            opacity: 1,
          },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '&:hover': {
            background:
              ownerState.color && ownerState.color !== 'default'
                ? alpha(theme.palette[ownerState.color].main, theme.palette.action.hoverOpacity)
                : theme.palette.action.hover,
          },
          '&:focus': {
            background:
              ownerState.color && ownerState.color !== 'default'
                ? alpha(theme.palette[ownerState.color].main, 0.3)
                : theme.palette.action.focus,
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '&:hover': {
            background:
              ownerState.color && ownerState.color !== 'default'
                ? alpha(theme.palette[ownerState.color].main, theme.palette.action.hoverOpacity)
                : theme.palette.action.hover,
          },
          '&:focus': {
            background:
              ownerState.color && ownerState.color !== 'default'
                ? alpha(theme.palette[ownerState.color].main, 0.3)
                : theme.palette.action.focus,
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          marginLeft: ownerState.variant === 'outlined' ? 0 : undefined,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          marginBottom: '5px',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          marginTop: -4,
          marginLeft: ownerState.variant === 'outlined' ? -14 : undefined,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: '8px',
          height: ownerState.variant === 'outlined' ? '44px' : undefined,
        }),
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
          height: '44px',
          marginTop: 10,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:focus': {
            border: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:focus': {
            border: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          '&.MuiBadge-root .MuiBadge-badge': { borderRadius: '100px', height: '8px', width: '8px' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: colors.tooltip[theme.palette.mode],
          color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
        }),
        arrow: ({ theme }) => ({
          color: colors.tooltip[theme.palette.mode],
        }),
      },
    },
  },
};

export const commonPaletteOptions = {
  common: { black: '#000', white: '#fff' },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    A100: '#F5F5F5',
    A200: '#EEEEEE',
    A400: '#BDBDBD',
    A700: '#616161',
  },
  tonalOffset: 0.5,
  contrastThreshold: 4.5,
};

export const dsCommonThemeOptions: ThemeOptions = {
  typography: {
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#656565',
    },
  },
  components: {
    MuiIconButton: {
      defaultProps: {
        disableRipple: false,
        disableFocusRipple: false,
        disableTouchRipple: false,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
        disableTouchRipple: false,
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          info: <Info />,
          error: <ErrorIcon />,
          success: <CheckCircleIcon />,
          warning: <ReportProblemIcon />,
        },
      },
    },
  },
};
